import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ContractorDashboard from './ContractorDashboard';

/**
 * Admin-only preview page.
 * Opened in a new tab via /contractor/preview?token=xxx
 * Decodes the JWT payload to get contractor info, stores the token
 * in localStorage so ContractorApiService picks it up, then renders
 * the full ContractorDashboard with a visible "Admin Preview" banner.
 */
const ContractorPreviewPage = () => {
  const [searchParams] = useSearchParams();
  const [contractor, setContractor] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError('No preview token provided.');
      return;
    }

    try {
      // Decode the JWT payload (middle segment, base64url)
      const payloadBase64 = token.split('.')[1];
      const payloadJson = atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/'));
      const claims = JSON.parse(payloadJson);

      // Map JWT claims to the contractor object ContractorDashboard expects
      const contractorUser = {
        id: claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] || claims.sub,
        contractorName: claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || claims.name || 'Contractor',
        email: claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || claims.email || '',
        xeroContactId: claims['XeroContactId'] || '',
        tenantId: claims['TenantId'] || '',
      };

      // Store so ContractorApiService can find it
      localStorage.setItem('contractorToken', token);
      localStorage.setItem('contractorUser', JSON.stringify(contractorUser));

      setContractor(contractorUser);
    } catch (err) {
      console.error('Failed to decode preview token:', err);
      setError('Invalid preview token. Please try opening the portal again from the admin panel.');
    }
  }, [searchParams]);

  const handleClose = () => {
    // Clean up preview credentials and close the tab
    localStorage.removeItem('contractorToken');
    localStorage.removeItem('contractorUser');
    window.close();
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center">
          <p className="text-red-600 font-medium mb-4">{error}</p>
          <button
            onClick={() => window.close()}
            className="px-4 py-2 bg-gray-200 rounded-lg text-gray-700 hover:bg-gray-300"
          >
            Close Tab
          </button>
        </div>
      </div>
    );
  }

  if (!contractor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Admin Preview Banner */}
      <div className="bg-amber-400 text-amber-900 text-center py-2 px-4 font-semibold text-sm flex items-center justify-center gap-3">
        <span>👁️ ADMIN PREVIEW — viewing as {contractor.contractorName}</span>
        <button
          onClick={handleClose}
          className="bg-amber-600 hover:bg-amber-700 text-white text-xs px-3 py-1 rounded-full"
        >
          Close Preview
        </button>
      </div>

      <ContractorDashboard contractor={contractor} onLogout={handleClose} />
    </div>
  );
};

export default ContractorPreviewPage;
